import React, { FormEvent, useEffect, useState } from 'react';
import { PrismBanner, PrismButton, PrismInputText } from '@prism-ui/react';
import useFetch from '../../../../hooks/useFetch';
import axios from 'axios';
import Loader from '../../../Loader';
import ErrorMessage from '../../../ErrorMessage';
import PermissionLevel from '../../../../data/PermissionLevel';
import { toast } from '../../../Toaster/toast';
import './RoleTenure.css';
import AdminPane from '../../AdminPane';

// Define props interface
interface RoleTenureProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  currentUser?: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  params?: any;
}

// Define the Chapter type
type Chapter = {
  id: number;
  location: string;
  distro: string;
};

// Define the ApplicantType type
type ApplicantType = {
  id: number;
  applicant_type: string;
};

// Define the RoleTenureData type
type RoleTenureData = {
  id?: number;
  chapter_id: number;
  applicant_type_id: number;
  tenure_months: number;
};

// Role display names mapping
const ROLE_NAMES: { [key: number]: string } = {
  1: 'Mentee',
  2: 'Mentor',
  3: 'TCM',
};

const ROLE_ORDER = [1, 2, 3]; // Display order: Mentee, Mentor, TCM

const RoleTenure: React.FC<RoleTenureProps> = ({ currentUser }) => {
  const {
    data: chapters,
    loading: chaptersLoading,
    error: chaptersError,
  } = useFetch<Chapter[]>('/api/chapters');

  const {
    data: applicantTypes,
    loading: typesLoading,
    error: typesError,
  } = useFetch<ApplicantType[]>('/api/application_types');

  const {
    data: roleTenureSettings,
    loading: tenureLoading,
    error: tenureError,
    refresh,
  } = useFetch<RoleTenureData[]>('/api/role_tenure/all');

  const [tenureData, setTenureData] = useState<RoleTenureData[]>([]);
  const [originalTenureData, setOriginalTenureData] = useState<RoleTenureData[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [sortAsc, setSortAsc] = useState(true);

  // Check if user is an officer (restricted access)
  const isOfficer = currentUser?.Role?.permission_level === PermissionLevel.Officer;
  const userChapterId = currentUser?.chapter_id;

  // Function to check if user can edit a specific chapter
  const canEditChapter = (chapterId: number): boolean => {
    if (!isOfficer) {
      // Admins and higher can edit all chapters
      return true;
    }

    // Officers can only edit their own chapter
    return chapterId === userChapterId;
  };

  // Initialize tenure data when all data is loaded
  useEffect(() => {
    if (chapters && applicantTypes && roleTenureSettings) {
      const tenureMap = new Map<string, RoleTenureData>();

      // Map existing settings
      roleTenureSettings.forEach((setting) => {
        const key = `${setting.chapter_id}-${setting.applicant_type_id}`;
        tenureMap.set(key, setting);
      });

      // Create entries for all chapter/role combinations
      const allTenureData: RoleTenureData[] = [];
      
      // Filter out Unassigned chapter (id 0)
      const activeChapters = chapters.filter(c => c.id !== 0);
      
      activeChapters.forEach((chapter) => {
        applicantTypes.forEach((type) => {
          const key = `${chapter.id}-${type.id}`;
          const existing = tenureMap.get(key);

          if (existing) {
            allTenureData.push(existing);
          } else {
            // Create default entry with 24 months
            allTenureData.push({
              chapter_id: chapter.id,
              applicant_type_id: type.id,
              tenure_months: 24,
            });
          }
        });
      });

      setTenureData(allTenureData);
      setOriginalTenureData(JSON.parse(JSON.stringify(allTenureData)));
      setHasChanges(false);
    }
  }, [chapters, applicantTypes, roleTenureSettings]);

  // Check for changes whenever tenureData changes
  useEffect(() => {
    const hasChangesNow = JSON.stringify(tenureData) !== JSON.stringify(originalTenureData);
    setHasChanges(hasChangesNow);
  }, [tenureData, originalTenureData]);

  const updateTenure = (
    chapterId: number,
    applicantTypeId: number,
    value: number
  ) => {
    // Check if user is authorized to edit this chapter
    if (!canEditChapter(chapterId)) {
      toast.error('You are not authorized to edit this chapter\'s tenure settings.');
      return;
    }

    // Validate value
    if (value < 0) {
      toast.error('Tenure months must be 0 or greater.');
      return;
    }

    setTenureData((prev) =>
      prev.map((item) =>
        item.chapter_id === chapterId && item.applicant_type_id === applicantTypeId
          ? { ...item, tenure_months: value }
          : item
      )
    );
  };

  // Check if a specific field has been changed from its original value
  const hasFieldChanged = (chapterId: number, applicantTypeId: number): boolean => {
    const currentItem = tenureData.find(
      (item) => item.chapter_id === chapterId && item.applicant_type_id === applicantTypeId
    );
    const originalItem = originalTenureData.find(
      (item) => item.chapter_id === chapterId && item.applicant_type_id === applicantTypeId
    );
    
    if (!currentItem || !originalItem) return false;
    
    return currentItem.tenure_months !== originalItem.tenure_months;
  };

  // Reset changes to original values
  const handleReset = () => {
    setTenureData(JSON.parse(JSON.stringify(originalTenureData)));
    setHasChanges(false);
  };

  const submit = async (e?: FormEvent) => {
    e?.preventDefault();

    setSubmitting(true);

    try {
      // Only send chapters the user is authorized to edit
      const settingsToSave = tenureData.filter((item) =>
        canEditChapter(item.chapter_id)
      );

      if (settingsToSave.length === 0) {
        toast.error('No authorized changes to save.');
        setSubmitting(false);
        return;
      }

      await axios.put('/api/role_tenure/all', {
        tenureSettings: settingsToSave,
      });

      await refresh();
      toast.success('Role tenure settings have been updated successfully.');
      
      // Update original data to reflect saved state
      setOriginalTenureData(JSON.parse(JSON.stringify(tenureData)));
      setHasChanges(false);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      console.error('Error updating role tenure settings:', error);
      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        'Failed to update role tenure settings';
      toast.error(errorMessage);
    } finally {
      window.setTimeout(() => setSubmitting(false), 500);
    }
  };

  const getTenureValue = (chapterId: number, applicantTypeId: number): number => {
    const item = tenureData.find(
      (d) => d.chapter_id === chapterId && d.applicant_type_id === applicantTypeId
    );
    return item?.tenure_months ?? 0;
  };

  // Check if user has any editable chapters
  const hasEditableChapters = tenureData.some((item) =>
    canEditChapter(item.chapter_id)
  );

  // Get sorted list of unique chapters from tenure data
  const sortedChapters = React.useMemo(() => {
    const uniqueChapterIds = Array.from(
      new Set(tenureData.map((item) => item.chapter_id))
    );
    
    return uniqueChapterIds
      .map((id) => chapters?.find((c) => c.id === id))
      .filter((c): c is Chapter => c !== undefined)
      .sort((a, b) => {
        const comparison = a.location.localeCompare(b.location);
        return sortAsc ? comparison : -comparison;
      });
  }, [tenureData, chapters, sortAsc]);

  const toggleSort = () => {
    setSortAsc(!sortAsc);
  };

  if (chaptersLoading || typesLoading || tenureLoading) {
    return <Loader />;
  }

  if (chaptersError || typesError || tenureError) {
    return (
      <ErrorMessage
        error={
          (chaptersError || typesError || tenureError)?.toString() ||
          'Unknown error'
        }
      />
    );
  }

  return (
    <AdminPane as="div" width="narrow" className="admin role-tenure-container">
      <form onSubmit={submit} className="role-tenure-form">
        <PrismBanner tone="info">
          <p className="role-tenure-help-text">
            These values control what the applicants see as part of the application
            flow. It also is what each application rater will see when rating
            applications.
          </p>
        </PrismBanner>

      <table className="role-tenure-table">
        <thead>
          <tr>
            <th onClick={toggleSort} className="role-tenure-sort-header">
              Chapter {sortAsc ? '▲' : '▼'}
            </th>
            {ROLE_ORDER.map((roleId) => (
              <th key={roleId}>
                {ROLE_NAMES[roleId]}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedChapters.map((chapter) => {
            const canEdit = canEditChapter(chapter.id);

            return (
              <tr
                key={chapter.id}
                className={!canEdit ? 'role-tenure-row-readonly' : ''}
              >
                <td>
                  <strong>{chapter.location}</strong>
                  {!canEdit && (
                    <small className="role-tenure-readonly-note">
                      <em>(Read-only)</em>
                    </small>
                  )}
                </td>
                {ROLE_ORDER.map((roleId) => {
                  const value = getTenureValue(chapter.id, roleId);
                  return (
                    <td key={roleId}>
                      <PrismInputText
                        type="number"
                        min="0"
                        step="1"
                        hide-label
                        label={`${ROLE_NAMES[roleId]} tenure in months for ${chapter.location}`}
                        value={value}
                        onPrismInput={(e: CustomEvent<{ value: string }>) => {
                          const newValue = parseInt(e.detail.value, 10);
                          if (!isNaN(newValue)) {
                            updateTenure(chapter.id, roleId, newValue);
                          }
                        }}
                        disabled={!canEdit}
                        className={
                          !canEdit
                            ? 'role-tenure-input role-tenure-input-readonly'
                            : hasFieldChanged(chapter.id, roleId)
                            ? 'role-tenure-input role-tenure-input-changed'
                            : 'role-tenure-input'
                        }
                      />
                      <small className="role-tenure-unit">months</small>
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>

      <div className="role-tenure-actions">
        <PrismButton
          tone="secondary"
          display="outline"
          onPrismClick={handleReset}
          disabled={!hasChanges || submitting}
        >
          <span>Reset Changes</span>
        </PrismButton>
        <div className="role-tenure-actions-status">
          {hasChanges && (
            <strong className="role-tenure-status-warning">You have unsaved changes</strong>
          )}
          {!hasEditableChapters && !hasChanges && (
            <span className="role-tenure-status-info">
              <strong>Note:</strong> You have read-only access to these records.
            </span>
          )}
        </div>
        <PrismButton
          tone="primary"
          display="fill"
          loading={submitting}
          disabled={submitting || !hasChanges || !hasEditableChapters}
          onPrismClick={() => submit()}
          title={
            !hasEditableChapters
              ? "You don't have permission to edit any chapters"
              : ''
          }
        >
          {/* Wrapped so the label is never the host's swapped-out only child. As a bare text
              node the swap leaves "Saving..." stranded beside the rendered button, which is
              what made the label look off-centre — see Design-Lab "cohort-action-buttons". */}
          <span>{submitting ? 'Saving...' : 'Save All Changes'}</span>
        </PrismButton>
      </div>
      </form>
    </AdminPane>
  );
};

export default RoleTenure;
